import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AddressForm from "@components/AddressManager/components/AddressForm";
import { Button, CircularProgress, TextField } from "@material-ui/core";
import {
  createUserAddress,
  getCasesByUser,
  searchUserByEmail,
  updateUserAddress,
} from "@store/admin/adminAction";
import { Case } from "@types-runway/case";
import { RootState, UserSearchData } from "@types-runway/state";
import { Address } from "@types-runway/user";

import CaseResults from "./CaseResults";

const UserSearch = () => {
  const [email, setEmail] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [addressFormIsOpen, setAddressFormIsOpen] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState<Address | undefined>(undefined);
  const dispatch = useDispatch();
  const userData = useSelector<RootState, UserSearchData>((state) => state.admin.users);
  const cases = useSelector<RootState, Case[]>((state) => state.admin.cases);
  const isSearchingUsers = useSelector<RootState, boolean>(
    (state) => state.admin.isSearchingUsers
  );

  const findUser = () => {
    dispatch(searchUserByEmail({ email }));
  };

  const openAddressForm = (address?: Address) => {
    setAddressToEdit(address);
    setAddressFormIsOpen(true);
  };

  const submitAddress = (address: Address) => {
    if (addressToEdit?.id) {
      // Edit in place — keep the existing id so the backend updates the row
      // instead of creating a near-duplicate (ENG-473).
      dispatch(updateUserAddress({ ...address, id: addressToEdit.id, UserId: userData.id }));
    } else {
      dispatch(createUserAddress({ ...address, UserId: userData.id }));
    }
    setAddressToEdit(undefined);
    setAddressFormIsOpen(false);
  };

  useEffect(() => {
    if (userData.id) {
      dispatch(getCasesByUser(userData.id));
    }
  }, [userData.id]);

  return (
    <section>
      <h2>Search User by Email 🕵️</h2>
      <div className="noMargin">
        <TextField
          type="email"
          id="email"
          value={email}
          onChange={(evt) => setEmail(evt.target.value)}
          label="Email"
        />
        <Button
          className="nonMaterialAlignment"
          variant="contained"
          color="primary"
          onClick={findUser}
          disabled={isSearchingUsers}
        >
          {isSearchingUsers ? <CircularProgress size={20} /> : "Search 🔍"}
        </Button>
      </div>
      {isSearchingUsers && <p>Searching...</p>}
      {userData && (
        <div className="noMargin">
          <div
            style={{ cursor: "pointer", marginLeft: 0 }}
            onClick={() => {
              setShowDetails(!showDetails);
            }}
          >
            <h3>User Info ⬇️</h3>
            <p>Click to show/hide</p>
          </div>
          <code style={{ display: showDetails ? "block" : "none" }}>
            <pre>{JSON.stringify(userData, null, 2)}</pre>
          </code>
          {showDetails ? (
            <>
              {!addressFormIsOpen ? (
                <>
                  {" "}
                  <h2>Addresses</h2>
                  {userData.addresses?.map((address) => (
                    <div key={address.id}>
                      <br></br>
                      <p>
                        <b>Street:</b> {address.street}
                      </p>
                      <p>
                        <b>Secondary:</b> {address.secondary}
                      </p>
                      <p>
                        <b>City:</b> {address.city}
                      </p>
                      <p>
                        <b>State:</b> {address.state}
                      </p>
                      <p>
                        <b>Zip Code:</b> {address.postalCode}
                      </p>
                      <button onClick={() => openAddressForm(address)}>Edit address ✏️</button>
                      <hr></hr>
                    </div>
                  ))}
                  <button onClick={() => openAddressForm()}>Add new address</button>
                </>
              ) : (
                <div style={{ maxWidth: "800px" }}>
                  <AddressForm
                    closeForm={() => {
                      setAddressToEdit(undefined);
                      setAddressFormIsOpen(false);
                    }}
                    onSubmit={submitAddress}
                    address={addressToEdit}
                    enableCardDecor
                  />
                </div>
              )}
              <hr></hr>
              <h2>Cases</h2>
              <CaseResults cases={cases} />
            </>
          ) : null}
        </div>
      )}
    </section>
  );
};

export default UserSearch;
